
import { useState, useEffect } from 'react';

/**
 * Types for lesson content structure
 */
interface ContentSection {
  title: string;
  text: string;
}

interface KnowledgeCheck {
  type: string;
  question: string;
  options: string[];
  correct_answer: number;
  explanation: string;
}

interface LessonContentData {
  sections: ContentSection[];
  objectives: string[];
  knowledgeChecks: KnowledgeCheck[];
  rewards?: {
    points?: number;
    badges?: string[];
  };
}

/**
 * Hook to parse and normalize lesson content from various formats
 * Enhanced to handle multiple content formats and provide better debugging
 * @param rawContent - The raw lesson content (string or object)
 * @param fallbackObjectives - Optional fallback objectives if not in content
 * @returns Parsed and normalized lesson content
 */
export function useLessonContent(
  rawContent: string | object | null, 
  fallbackObjectives: string[] = []
): LessonContentData {
  const [parsedContent, setParsedContent] = useState<LessonContentData>({
    sections: [],
    objectives: fallbackObjectives,
    knowledgeChecks: [],
    rewards: { points: 0 }
  });

  useEffect(() => {
    if (!rawContent) {
      console.log("📝 No content provided to parse");
      return;
    }

    try {
      console.log("🔍 Raw content type:", typeof rawContent);
      console.log("🔍 Raw content:", rawContent);

      // Handle different content formats more robustly
      let contentObj: any;
      
      if (typeof rawContent === 'string') {
        // Try to parse JSON string
        try {
          contentObj = JSON.parse(rawContent);
          console.log("✅ Successfully parsed JSON string");
        } catch (parseError) {
          console.error("❌ Failed to parse JSON string:", parseError);
          // If it's not valid JSON, treat as plain text and create a basic structure
          contentObj = {
            sections: [
              {
                title: "Lesson Content",
                text: rawContent
              }
            ]
          };
          console.log("🔄 Created basic structure from string content");
        }
      } else if (typeof rawContent === 'object' && rawContent !== null) {
        contentObj = rawContent;
        console.log("✅ Using object content directly");
      } else {
        throw new Error("Invalid content format");
      }
      
      console.log("📊 Parsed content object:", contentObj);
      
      // Extract content with improved nested structure handling
      // Handle both direct content and nested content patterns
      const actualContent = contentObj?.content || contentObj;
      
      console.log("🔍 Actual content structure:", actualContent);
      
      // Extract sections with multiple fallback patterns
      let sections: ContentSection[] = [];
      if (actualContent?.sections && Array.isArray(actualContent.sections)) {
        sections = actualContent.sections;
      } else if (contentObj?.sections && Array.isArray(contentObj.sections)) {
        sections = contentObj.sections;
      } else if (actualContent?.text || contentObj?.text) {
        // Create a single section from text content
        sections = [
          {
            title: actualContent?.title || contentObj?.title || "Lesson Content",
            text: actualContent?.text || contentObj?.text || ""
          }
        ];
      }
      
      // Extract objectives with multiple fallback patterns
      const objectives = actualContent?.objectives || 
                        contentObj?.objectives || 
                        fallbackObjectives || 
                        [];
      
      // Extract knowledge checks with multiple fallback patterns
      let knowledgeChecks: KnowledgeCheck[] = [];
      const rawKnowledgeChecks = actualContent?.knowledgeChecks || 
                                actualContent?.knowledge_checks ||
                                contentObj?.knowledgeChecks ||
                                contentObj?.knowledge_checks ||
                                contentObj?.quiz?.questions || 
                                [];
      
      // Transform knowledge checks to ensure they have the correct structure
      if (Array.isArray(rawKnowledgeChecks)) {
        knowledgeChecks = rawKnowledgeChecks.map((check: any) => ({
          type: check.type || 'multiple_choice',
          question: check.question || '',
          options: Array.isArray(check.options) ? check.options : [],
          correct_answer: check.correctAnswer !== undefined ? check.correctAnswer : check.correct_answer || 0,
          explanation: check.explanation || ''
        }));
      }
      
      // Extract rewards with fallbacks
      const rewards = {
        points: actualContent?.rewards?.points || 
                contentObj?.rewards?.points || 
                50, // Default fallback points
        badges: actualContent?.rewards?.badges || 
                contentObj?.rewards?.badges ||
                (contentObj?.rewards?.badge ? [contentObj.rewards.badge] : [])
      };

      console.log("✅ Successfully parsed lesson content:", { 
        sections: sections.length, 
        objectives: objectives.length, 
        knowledgeChecks: knowledgeChecks.length, 
        rewards,
        sectionsData: sections.map(s => ({ title: s.title, textLength: s.text?.length || 0 })),
        knowledgeChecksData: knowledgeChecks.map(kc => ({ question: kc.question.substring(0, 50) + "..." }))
      });

      setParsedContent({
        sections: sections || [],
        objectives: Array.isArray(objectives) ? objectives : [],
        knowledgeChecks: knowledgeChecks || [],
        rewards
      });
    } catch (error) {
      console.error("❌ Error parsing lesson content:", error);
      console.log("🔍 Raw content that failed to parse:", rawContent);
      
      // Enhanced fallback: try to extract any usable content
      let fallbackSections: ContentSection[] = [];
      
      if (typeof rawContent === 'string' && rawContent.trim()) {
        fallbackSections = [
          {
            title: "Lesson Content",
            text: rawContent.trim()
          }
        ];
      } else if (typeof rawContent === 'object' && rawContent !== null) {
        // Try to extract any text content from the object
        const obj = rawContent as any;
        if (obj.title || obj.text || obj.description) {
          fallbackSections = [
            {
              title: obj.title || "Lesson Content",
              text: obj.text || obj.description || "Content is being developed for this lesson."
            }
          ];
        }
      }
      
      // Use enhanced fallback values on error
      setParsedContent({
        sections: fallbackSections,
        objectives: Array.isArray(fallbackObjectives) ? fallbackObjectives : [],
        knowledgeChecks: [],
        rewards: { points: 50 }
      });
    }
  }, [rawContent, fallbackObjectives]);

  return parsedContent;
}
